import React from "react";
import {
  useCreateProjectCategory,
  useDeleteProjectCategory,
  useProjectCategories,
  useUpdateProjectCategory,
} from "../../../api/useProjectCategories";
import { buildOptions } from "@ui/Filter";
import type { BaseOption } from "@ui/ReactSelect/useFormatOptionLabel";
import type { MultiValue } from "react-select";

export const useEquipmentsInput = (
  projectId: string,
  selectedEntries: { equipmentIds: string[] }[],
) => {
  const equipments = useProjectCategories(projectId, "EQUIPMENT");
  const createEquipment = useCreateProjectCategory(projectId, "EQUIPMENT");
  const updateEquipment = useUpdateProjectCategory(projectId, "EQUIPMENT");
  const deleteEquipment = useDeleteProjectCategory(projectId, "EQUIPMENT");
  const [dirty, setDirty] = React.useState(false);
  const equipmentOptions = React.useMemo(
    () => buildOptions(equipments.map(e => ({ label: e.name, value: e.id }))),
    [equipments],
  );

  const equipmentDefaultValues = React.useMemo(
    () =>
      selectedEntries.flatMap((target: (typeof selectedEntries)[number]) =>
        target.equipmentIds?.reduce<BaseOption[]>((acc, id) => {
          const equipment = equipments.find(e => e.id === id);
          if (equipment) {
            acc.push({ label: equipment.name, value: equipment.id });
          }
          return acc;
        }, []),
      ),
    [equipments, selectedEntries],
  );

  const [selectedEquipments, setSelectedEquipments] =
    React.useState<MultiValue<BaseOption>>(equipmentDefaultValues);

  const handleEquipmentChanged = React.useCallback(
    (values: MultiValue<BaseOption>) => {
      setSelectedEquipments(values);
      setDirty(
        values.length !== equipmentDefaultValues.length ||
          values.some(({ value }) => !equipmentDefaultValues.find(({ value: v }) => value === v)),
      );
    },
    [equipmentDefaultValues],
  );

  const handleEquipmentCreated = React.useCallback(
    async (value: string) => {
      const { body: option } = await createEquipment(value.replace(",", ""));
      setSelectedEquipments(state => [...state, { value: option.id, label: option.name }]);
      setDirty(true);
    },
    [createEquipment],
  );

  const handleEquipmentDeleted = React.useCallback(
    async (option: BaseOption) => {
      if (option.value) {
        await deleteEquipment(option.value);

        if (selectedEquipments.find(({ value: id }) => id === option.value)) {
          setDirty(true);
        }

        setSelectedEquipments(state => state.filter(({ value: id }) => id !== option.value));
      }
    },
    [deleteEquipment, selectedEquipments],
  );

  const handleEquipmentEdited = React.useCallback(
    async ({ value }: BaseOption, label: string) => {
      if (value) {
        const escapedLabel = label.replace(",", "");
        await updateEquipment(value, escapedLabel);

        if (selectedEquipments.find(({ value: id }) => id === value)) {
          setDirty(true);
        }

        setSelectedEquipments(state => {
          const i = state.findIndex(({ value: id }) => id === value);
          return state.map((el, j) => (i === j ? { label: escapedLabel, value } : el));
        });
      }
    },
    [updateEquipment, selectedEquipments],
  );

  const resetValues = React.useCallback(() => {
    setSelectedEquipments(equipmentDefaultValues);
    setDirty(false);
  }, [equipmentDefaultValues]);

  return {
    equipments,
    equipmentOptions,
    equipmentDefaultValues,
    selectedEquipments,
    dirty,
    resetValues,
    handleEquipmentChanged,
    handleEquipmentCreated,
    handleEquipmentDeleted,
    handleEquipmentEdited,
  };
};
